"use client";
import * as React from "react";
import * as Popover from "@radix-ui/react-popover";
import { ChevronLeft, ChevronRight, X, Calendar } from "lucide-react";
import { cn } from "../../lib/utils";
import { toDateString } from "../date-utils";

export interface DateRangeInputProps {
  value?: [string, string] | null;
  onChange?: (range: [string, string] | null) => void;
  placeholder?: string;
  format?: string;
  theme?: "dark" | "light" | "auto";
  primaryColor?: string;
  disabled?: boolean;
  minDate?: Date | string;
  maxDate?: Date | string;
  /**
   * Lets `minDate` reach into the past. Off by default because this picker was
   * built for booking, where a past day is never selectable and `minDate` only
   * ever narrows the future; report screens query the past and opt in.
   */
  allowPastDates?: boolean;
  unavailableDates?: Set<string>;
  className?: string;
  error?: string;
  showLabels?: boolean;
  startLabel?: string;
  endLabel?: string;
  showToday?: boolean;
  textColor?: string;
  // Legacy props for backward compatibility
  startDate?: string;
  endDate?: string;
  onDateRangeChange?: (startDate: string, endDate: string) => void;
}

function hasUnavailableDatesInRange(
  start: string,
  end: string,
  unavailableDates: Set<string>,
): boolean {
  const startDate = new Date(start + "T00:00:00");
  const endDate = new Date(end + "T00:00:00");
  const current = new Date(startDate);
  current.setDate(current.getDate() + 1);
  while (current < endDate) {
    if (unavailableDates.has(toDateString(current))) return true;
    current.setDate(current.getDate() + 1);
  }
  return false;
}

const DateRangeInput = React.forwardRef<HTMLDivElement, DateRangeInputProps>(
  (allProps, ref) => {
    const {
      value,
      onChange,
      placeholder = "Select date range",
      theme = "auto",
      primaryColor,
      disabled = false,
      minDate,
      maxDate,
      allowPastDates = false,
      unavailableDates = new Set(),
      className,
      error,
      showLabels = false,
      startLabel = "Check-in",
      endLabel = "Check-out",
      showToday = true,
      textColor,
      // Legacy props
      startDate,
      endDate,
      onDateRangeChange,
      ...props
    } = allProps;

    const [open, setOpen] = React.useState(false);
    const [hoveredDate, setHoveredDate] = React.useState<string | null>(null);
    const [currentMonth, setCurrentMonth] = React.useState(() => {
      const today = new Date();
      return new Date(today.getFullYear(), today.getMonth(), 1);
    });
    const [nextMonth, setNextMonth] = React.useState(() => {
      const today = new Date();
      return new Date(today.getFullYear(), today.getMonth() + 1, 1);
    });
    const [isMobile, setIsMobile] = React.useState(false);
    const triggerRef = React.useRef<HTMLDivElement>(null);

    const today = new Date();

    // Helper function to darken a hex color
    const darkenColor = (hex: string, percent: number = 10): string => {
      const num = parseInt(hex.replace("#", ""), 16);
      const r = Math.max(0, (num >> 16) - Math.round(2.55 * percent));
      const g = Math.max(0, ((num >> 8) & 0x00ff) - Math.round(2.55 * percent));
      const b = Math.max(0, (num & 0x0000ff) - Math.round(2.55 * percent));
      return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
    };

    // Helper function to add alpha to hex color
    const hexToRgba = (hex: string, alpha: number): string => {
      const num = parseInt(hex.replace("#", ""), 16);
      const r = (num >> 16) & 255;
      const g = (num >> 8) & 255;
      const b = num & 255;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // Handle legacy props
    const range = React.useMemo(() => {
      if (value) return value;
      if (startDate && endDate) return [startDate, endDate];
      return null;
    }, [value, startDate, endDate]);

    const handleChange = React.useCallback(
      (newRange: [string, string] | null) => {
        if (onChange) {
          onChange(newRange);
        } else if (onDateRangeChange && newRange) {
          onDateRangeChange(newRange[0], newRange[1]);
        }
      },
      [onChange, onDateRangeChange],
    );

    // Theme detection
    const isDark = React.useMemo(() => {
      if (theme === "dark") return true;
      if (theme === "light") return false;
      // Auto theme - check system preference
      if (typeof window !== "undefined") {
        return window.matchMedia("(prefers-color-scheme: dark)").matches;
      }
      return false;
    }, [theme]);

    const formatDate = (date: Date): string => {
      return toDateString(date);
    };

    const formatDisplayDate = (dateString: string) => {
      if (!dateString) return "";
      const date = new Date(dateString + "T00:00:00");
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    };

    const formatRangeDisplay = () => {
      if (!range || !range[0] || !range[1]) return placeholder;
      return `${formatDisplayDate(range[0])} - ${formatDisplayDate(range[1])}`;
    };

    const isDateInRange = (
      date: Date,
      start: string | undefined,
      end: string | undefined,
    ) => {
      if (!start || !end) return false;
      const dateStr = formatDate(date);
      return dateStr >= start && dateStr <= end;
    };

    const isDateSelected = (date: Date) => {
      if (!range) return false;
      const dateStr = formatDate(date);
      return (
        (range[0] && range[0] === dateStr) || (range[1] && range[1] === dateStr)
      );
    };

    const isDateStart = (date: Date) => {
      if (!range || !range[0]) return false;
      return formatDate(date) === range[0];
    };

    const isDateEnd = (date: Date) => {
      if (!range || !range[1]) return false;
      return formatDate(date) === range[1];
    };

    const isDateUnavailable = (date: Date) => {
      const dateStr = formatDate(date);
      return unavailableDates.has(dateStr);
    };

    const isDateDisabled = (date: Date) => {
      // Blocked days stay blocked whatever the bounds allow.
      if (isDateUnavailable(date)) return true;
      if (!allowPastDates) {
        const todayStart = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
        );
        if (date < todayStart) return true;
      }
      if (minDate && date < new Date(minDate)) return true;
      if (maxDate && date > new Date(maxDate)) return true;
      return false;
    };

    const isDateToday = (date: Date) => {
      if (!showToday) return false;
      return formatDate(date) === formatDate(today);
    };

    const isDateInHoverRange = (date: Date) => {
      if (!hoveredDate || !range || !range[0]) return false;
      const dateStr = formatDate(date);
      const startStr = range[0];
      const hoverStr = hoveredDate;

      // Check if date is between start and hover
      if (startStr < hoverStr) {
        return dateStr >= startStr && dateStr <= hoverStr;
      } else {
        return dateStr >= hoverStr && dateStr <= startStr;
      }
    };

    const handleDateClick = (date: Date) => {
      if (isDateDisabled(date)) return;

      const dateStr = formatDate(date);

      if (!range || !range[0]) {
        // First selection
        handleChange([dateStr, ""]);
      } else if (!range[1]) {
        const [rangeStart, rangeEnd] =
          range[0] && dateStr < range[0]
            ? [dateStr, range[0]]
            : [range[0], dateStr];

        if (
          rangeStart &&
          rangeEnd &&
          hasUnavailableDatesInRange(rangeStart, rangeEnd, unavailableDates)
        ) {
          handleChange([dateStr, ""]);
          return;
        }

        if (range[0] && dateStr < range[0]) {
          handleChange([dateStr, range[0]]);
        } else if (range[0]) {
          handleChange([range[0], dateStr]);
        }
        setOpen(false);
      } else {
        // New selection
        handleChange([dateStr, ""]);
      }
    };

    const handleDateHover = (date: Date) => {
      if (!range || !range[0] || range[1]) return;
      const dateStr = formatDate(date);
      setHoveredDate(dateStr);
    };

    const clearSelection = () => {
      handleChange(null);
    };

    const navigateMonths = (direction: "prev" | "next") => {
      const newCurrentMonth = new Date(currentMonth);
      if (direction === "prev") {
        newCurrentMonth.setMonth(newCurrentMonth.getMonth() - 1);
      } else {
        newCurrentMonth.setMonth(newCurrentMonth.getMonth() + 1);
      }
      setCurrentMonth(newCurrentMonth);
      setNextMonth(
        new Date(
          newCurrentMonth.getFullYear(),
          newCurrentMonth.getMonth() + 1,
          1,
        ),
      );
    };

    const getDaysInMonth = (date: Date) => {
      return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date: Date) => {
      return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    };

    // Keyboard navigation and responsive detection
    React.useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth < 768);
      };

      checkMobile();
      window.addEventListener("resize", checkMobile);
      return () => window.removeEventListener("resize", checkMobile);
    }, []);

    // Keyboard event handler
    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (!open) return;

      switch (event.key) {
        case "Escape":
          setOpen(false);
          triggerRef.current?.focus();
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          break;
      }
    };

    const renderCalendar = (month: Date) => {
      const daysInMonth = getDaysInMonth(month);
      const firstDay = getFirstDayOfMonth(month);
      const days = [];

      // Add empty cells for days before the first day of the month
      for (let i = 0; i < firstDay; i++) {
        days.push(<div key={`empty-${i}`} className="h-8" />);
      }

      // Add days of the month
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(month.getFullYear(), month.getMonth(), day);
        const isDisabled = isDateDisabled(date);
        const isSelected = isDateSelected(date);
        const isStart = isDateStart(date);
        const isEnd = isDateEnd(date);
        const isToday = isDateToday(date);
        const isInRange =
          range &&
          range[0] &&
          range[1] &&
          isDateInRange(date, range[0], range[1]);
        const isInHoverRange = isDateInHoverRange(date);

        // Build inline styles for primary color support
        const getDateStyle = (): React.CSSProperties | undefined => {
          if (!primaryColor) return undefined;

          if (isStart || isEnd) {
            return { backgroundColor: primaryColor, color: "white" };
          }
          if (isInRange && !isStart && !isEnd) {
            return {
              backgroundColor: hexToRgba(primaryColor, 0.15),
              color: isDark ? "white" : darkenColor(primaryColor, 30),
            };
          }
          if (isInHoverRange && !isSelected && !isInRange) {
            return {
              backgroundColor: hexToRgba(primaryColor, 0.1),
              borderColor: hexToRgba(primaryColor, 0.5),
              color: isDark ? "white" : darkenColor(primaryColor, 30),
            };
          }
          return undefined;
        };

        days.push(
          <button
            key={day}
            type="button"
            onClick={() => handleDateClick(date)}
            onMouseEnter={() => handleDateHover(date)}
            onMouseLeave={() => setHoveredDate(null)}
            disabled={isDisabled}
            className={cn(
              "relative h-8 w-8 rounded-md text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1",
              // Base styles
              isDark ? "text-white" : "text-gray-900",
              // Disabled state
              isDisabled && "opacity-30 cursor-not-allowed",
              // Today indicator
              isToday &&
                !isSelected &&
                !isInRange &&
                !isInHoverRange &&
                (isDark
                  ? "ring-1 ring-gray-400 text-white"
                  : "ring-1 ring-gray-400 text-gray-900"),
              // Selected states (only apply if no primaryColor)
              !primaryColor &&
                isStart &&
                (isDark
                  ? "bg-blue-400 text-white hover:bg-blue-500"
                  : "bg-blue-600 text-white hover:bg-blue-700"),
              !primaryColor &&
                isEnd &&
                (isDark
                  ? "bg-blue-400 text-white hover:bg-blue-500"
                  : "bg-blue-600 text-white hover:bg-blue-700"),
              // Range styles (only apply if no primaryColor)
              !primaryColor &&
                isInRange &&
                !isStart &&
                !isEnd &&
                (isDark
                  ? "bg-blue-400/20 text-white"
                  : "bg-blue-100 text-blue-900"),
              // Hover range preview (only apply if no primaryColor)
              !primaryColor &&
                isInHoverRange &&
                !isSelected &&
                !isInRange &&
                (isDark
                  ? "bg-blue-400/10 text-white border border-dashed border-blue-400/50"
                  : "bg-blue-50 text-blue-900 border border-dashed border-blue-300"),
              // Hover range with primaryColor needs border class
              primaryColor &&
                isInHoverRange &&
                !isSelected &&
                !isInRange &&
                "border border-dashed",
              // Hover state
              !isDisabled &&
                !isSelected &&
                !isInRange &&
                !isInHoverRange &&
                (isDark ? "hover:bg-gray-600" : "hover:bg-gray-100"),
            )}
            style={getDateStyle()}
          >
            {day}
          </button>,
        );
      }

      return days;
    };

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    return (
      <div ref={ref} className={cn("space-y-2", className)} {...props}>
        {showLabels && (
          <div className="flex justify-between text-sm font-medium">
            <span className={isDark ? "text-gray-300" : "text-gray-700"}>
              {startLabel}
            </span>
            <span className={isDark ? "text-gray-300" : "text-gray-700"}>
              {endLabel}
            </span>
          </div>
        )}

        <Popover.Root open={open} onOpenChange={setOpen}>
          <Popover.Trigger asChild>
            <div
              ref={triggerRef}
              onKeyDown={handleKeyDown}
              className={cn(
                disabled && "cursor-not-allowed",
                "flex h-10 w-full items-center justify-between rounded-lg border px-4 py-3 text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1",
                isDark
                  ? "border-cyan-400 bg-gray-600 text-white placeholder:text-gray-300 focus:ring-cyan-400"
                  : "border-gray-300 bg-white text-gray-900 placeholder:text-gray-500 focus:ring-blue-500",
                error &&
                  (isDark
                    ? "border-red-400 focus:ring-red-400"
                    : "border-red-500 focus:ring-red-500"),
                disabled && "opacity-50 cursor-not-allowed",
              )}
              style={{ minWidth: "250px" }}
            >
              <div className="flex items-center gap-0">
                <Calendar
                  className={cn(
                    "h-5 w-5 m-0 p-0 pr-1",
                    isDark ? "text-gray-400" : "text-gray-500",
                  )}
                />
                <span
                  className={cn(
                    "text-sm font-medium",
                    !textColor &&
                      (range && range[0] && range[1]
                        ? isDark
                          ? "text-white"
                          : "text-gray-900"
                        : isDark
                          ? "text-gray-400"
                          : "text-gray-500"),
                  )}
                  style={textColor ? { color: textColor } : undefined}
                >
                  {formatRangeDisplay()}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {range && range[0] && range[1] && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearSelection();
                    }}
                    className={cn(
                      "rounded-full p-1 transition-colors",
                      isDark
                        ? "hover:bg-gray-500 text-gray-400 hover:text-white"
                        : "hover:bg-gray-100 text-gray-500 hover:text-gray-700",
                    )}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          </Popover.Trigger>

          <Popover.Portal>
            <Popover.Content
              className={cn(
                "z-[10000] w-auto rounded-lg border p-4 shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
                isDark
                  ? "border-gray-600 bg-gray-700"
                  : "border-gray-200 bg-white",
              )}
              sideOffset={8}
            >
              <div className={cn("flex gap-8", isMobile && "flex-col gap-4")}>
                {/* First Month */}
                <div className={cn("w-64", isMobile && "w-full")}>
                  <div className="flex items-center justify-between mb-4">
                    <button
                      type="button"
                      onClick={() => navigateMonths("prev")}
                      className={cn(
                        "rounded-md p-1 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1",
                        isDark
                          ? "hover:bg-gray-600 text-gray-300 focus:ring-cyan-400"
                          : "hover:bg-gray-100 text-gray-600 focus:ring-blue-500",
                      )}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <h3
                      className={cn(
                        "text-sm font-semibold",
                        isDark ? "text-white" : "text-gray-900",
                      )}
                    >
                      {monthNames[currentMonth.getMonth()]}{" "}
                      {currentMonth.getFullYear()}
                    </h3>
                    <div className="w-6" /> {/* Spacer for alignment */}
                  </div>

                  {/* Days of week header */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
                      <div
                        key={day + index}
                        className={cn(
                          "h-8 flex items-center justify-center text-xs font-medium",
                          isDark ? "text-gray-400" : "text-gray-500",
                        )}
                      >
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {renderCalendar(currentMonth)}
                  </div>
                </div>

                {/* Second Month */}
                <div className={cn("w-64", isMobile && "w-full")}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-6" /> {/* Spacer for alignment */}
                    <h3
                      className={cn(
                        "text-sm font-semibold",
                        isDark ? "text-white" : "text-gray-900",
                      )}
                    >
                      {monthNames[nextMonth.getMonth()]}{" "}
                      {nextMonth.getFullYear()}
                    </h3>
                    <button
                      type="button"
                      onClick={() => navigateMonths("next")}
                      className={cn(
                        "rounded-md p-1 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1",
                        isDark
                          ? "hover:bg-gray-600 text-gray-300 focus:ring-cyan-400"
                          : "hover:bg-gray-100 text-gray-600 focus:ring-blue-500",
                      )}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Days of week header */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
                      <div
                        key={day + index}
                        className={cn(
                          "h-8 flex items-center justify-center text-xs font-medium",
                          isDark ? "text-gray-400" : "text-gray-500",
                        )}
                      >
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {renderCalendar(nextMonth)}
                  </div>
                </div>
              </div>

              <Popover.Arrow
                className={cn(
                  "fill-current",
                  isDark ? "text-gray-700" : "text-white",
                )}
              />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>

        {error && (
          <p
            className={cn("text-sm", isDark ? "text-red-400" : "text-red-600")}
          >
            {error}
          </p>
        )}
      </div>
    );
  },
);

DateRangeInput.displayName = "DateRangeInput";

export { DateRangeInput };
